import PortfolioInputCoverageSummary from './PortfolioInputCoverageSummary';
import React, { useState, useEffect, useMemo } from 'react';
import PortfolioBacktestTaxComparison from './PortfolioBacktestTaxComparison';
import { Link, useSearchParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout';
import Alert from '@/components/UI/Alert';
import DataTable from '@/components/UI/DataTable';
import FormSelect from '@/components/UI/FormSelect';
import type { CellContext, ColumnDef } from '@/components/UI/dataTableConfig';
import PerformanceChart from '@/components/Chart/PerformanceChart';
import { trimDecimals, getDatePieces, getUnixTime } from '@einhaus/meteoric-helpers';
import { isEvaluatedBacktestTrade, dailyBalanceSnapshots } from '@einhaus/project314-shared';
import dayjs from 'dayjs';
import './PortfolioBacktestStats.tailwind.css';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc, trpcPostUnbatched } from '@/api/trpc';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { SimulateBacktestTradesStats, EntityType, EntityObjectCompressed } from '@einhaus/project314-types';
import { formatUnderlyingBias } from '@/utils/tradeSemantics';
import {
    calculateVolatilityFromPostTradeBalances,
    calculateSortinoRatioFromPostTradeBalances,
    calculatePainIndexFromPostTradeBalances,
    calculatePortfolioCagr,
    calculateMaxConcurrentTrades
} from '@/utils/backtestHelpers.ts';
import { useFetchEntities } from '@/hooks/useFetchEntities';
import {
    getTimingEdgeCoverageLabel,
    getTimingEdgeTooltip,
    TIMING_EDGE_DISCLAIMER
} from '@/pages/User/PortfolioBacktest/portfolioBacktestColumnConfig';
import { PortfolioBacktestEntryTimingProfiles } from './PortfolioBacktestEntryTimingProfiles';
import PortfolioBacktestRefreshControl from '../PortfolioBacktest/PortfolioBacktestRefreshControl';
import PortfolioBacktestRefreshSummary from '../PortfolioBacktest/PortfolioBacktestRefreshSummary';
import { indexPortfolioRefreshStatuses, resolvePortfolioRefreshStatus } from '../PortfolioBacktest/portfolioBacktestRefreshStatus';
import PriceEntrySetupSummary from '@/components/TradeLogic/PriceEntrySetupSummary';
import {
    buildPerformanceStatsRow,
    getPortfolioBacktestOptionRequests,
    getPortfolioBacktestPerformance as getPerformanceForTrades,
    groupPortfolioBacktestTrades,
    underlyingExposureGroups,
    type EntityStatsGrouping,
    type PerformanceStatsRow
} from './portfolioBacktestStatsGroups';

type ResearchWindowStat = { window: string; stats: SimulateBacktestTradesStats };

type PerformanceTrades = Parameters<typeof getPerformanceForTrades>[0]['trades'];

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
});

function formatCurrency(value: number): string {
    return currencyFormatter.format(value);
}

interface StrategyAttributionRow {
    strategy: string;
    window: string;
    candidateTrades: number;
    acceptedTrades: number;
    skippedTrades: number;
    acceptedProfit: number;
    profitContribution: number | null;
    averageTradeProfit: number | null;
    medianTradeProfit: number | null;
    winRate: number | null;
    profitFactor: number | null;
    returnOnRiskCapital: number | null;
    averageRiskCapital: number | null;
}

interface StrategyPerformanceValueProps {
    value: number | null;
    format: 'currency' | 'number' | 'percent';
    positiveThreshold?: number;
}

function StrategyPerformanceValue({ value, format, positiveThreshold = 0 }: StrategyPerformanceValueProps) {
    if (value === null || !Number.isFinite(value)) return <span>—</span>;

    const colorClass = value > positiveThreshold ? 'text-profit-green !text-[#29a740]' : value < positiveThreshold ? 'text-red-500' : '';
    const displayValue = format === 'currency' ? formatCurrency(value) : format === 'percent' ? `${value}%` : String(value);

    return <span className={colorClass}>{displayValue}</span>;
}

// Results must use the run's saved split, never the current date.
function computeResearchWindowStats(
    trades: PerformanceTrades,
    trainingEndDateTime: string | null,
    startingBalance: number
): ResearchWindowStat[] {
    if (trainingEndDateTime === null) return [];
    const developmentCutoffUnix = getUnixTime(trainingEndDateTime);
    const researchWindowStats: ResearchWindowStat[] = [];

    const trainingTrades = trades.filter((trade) => {
        const entryTimestamp = trade.transactions[0]?.timestamp;
        return typeof entryTimestamp === 'number' && entryTimestamp < developmentCutoffUnix;
    });

    const walkForwardTrades = trades.filter((trade) => {
        const entryTimestamp = trade.transactions[0]?.timestamp;
        return typeof entryTimestamp === 'number' && entryTimestamp >= developmentCutoffUnix;
    });

    if (trainingTrades.length) {
        researchWindowStats.push({
            window: 'Training (in-sample)',
            stats: getPerformanceForTrades({ trades: trainingTrades, startingBalance })
        });
    }

    if (walkForwardTrades.length) {
        researchWindowStats.push({
            window: 'Walk-forward (out-of-sample)',
            stats: getPerformanceForTrades({ trades: walkForwardTrades, startingBalance })
        });
    }

    return researchWindowStats;
}

const PortfolioBacktestStats: React.FC = () => {
    const [searchParams] = useSearchParams();
    const portfolioBacktestId = searchParams.get('id') ? parseInt(searchParams.get('id') as string, 10) : 0;

    const [error, setError] = useState<string | null>(null);
    const [entityGrouping, setEntityGrouping] = useState<EntityStatsGrouping>('all');

    // Derived stats types
    type DirectionStat = { direction: string; stats: SimulateBacktestTradesStats };
    type YearStat = { year: string; stats: SimulateBacktestTradesStats };
    type MonthStat = { month: string; stats: SimulateBacktestTradesStats };
    type QuarterStat = { quarter: string; stats: SimulateBacktestTradesStats };

    // (no-op placeholder removed) Derived stats are computed below when trades are available

    // Fetch backtest data
    const backtestQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioBacktest.queryOptions({ backtestId: portfolioBacktestId }),
            enabled: portfolioBacktestId > 0
        },
        {
            fallbackErrorMessage: 'Failed to fetch portfolio backtest',
            setError: (msg) => setError(msg)
        }
    );

    // Extract backtest data
    const backtest = backtestQuery.data?.backtest || null;
    const refreshQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioBacktestRefreshSchedules.queryOptions({ portfolioId: backtest?.portfolioId }),
            enabled: Boolean(backtest),
            refetchInterval: 60000
        },
        { fallbackErrorMessage: 'Failed to load refresh settings', showErrorToast: true }
    );
    const refreshStatus = backtest
        ? resolvePortfolioRefreshStatus(backtest, indexPortfolioRefreshStatuses(refreshQuery.data?.schedules ?? []))
        : undefined;
    const entryTimingProfiles = backtestQuery.data?.entryTimingProfiles ?? [];
    const frozenExitCandidateSelections = backtestQuery.data?.frozenExitCandidateSelections ?? [];
    const priceEntrySetups = backtestQuery.data?.priceEntrySetups;
    const strategyStats = backtestQuery.data?.strategyStats ?? [];

    const balancesForRiskFree = useMemo(() => {
        const balances = backtest?.postTradeBalances ?? [];
        return balances.slice().sort((a, b) => a.timestamp - b.timestamp);
    }, [backtest?.postTradeBalances]);

    const riskFreeStartDate = balancesForRiskFree[0]
        ? dayjs.unix(balancesForRiskFree[0].timestamp).format('YYYY-MM-DD')
        : backtest?.startDateTime || '';

    const riskFreeEndDate =
        balancesForRiskFree.length > 0
            ? dayjs.unix(balancesForRiskFree[balancesForRiskFree.length - 1].timestamp).format('YYYY-MM-DD')
            : backtest?.endDateTime || '';

    // Fetch risk-free rate for calculating sharpe and sortino ratios
    const riskFreeRateQuery = useAppQuery({
        ...trpc.reports.getAvgRiskFreeRatePerPeriod.queryOptions({
            startDate: riskFreeStartDate,
            endDate: riskFreeEndDate
        }),
        enabled: Boolean(riskFreeStartDate && riskFreeEndDate)
    });

    // Extract risk-free rate
    const riskFreeRate = riskFreeRateQuery.data?.avgRiskFreeRatePerPeriod || 0;

    // Fetch trades data
    const tradesQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioBacktestTrades.queryOptions({ portfolioBacktestId }),
            enabled: portfolioBacktestId > 0
        },
        {
            fallbackErrorMessage: 'Failed to fetch portfolio backtest trades',
            setError: (msg) => setError(msg)
        }
    );

    // Only completed trades with finite canonical economics can contribute to performance statistics.
    const rawTrades = useMemo(() => tradesQuery.data?.trades || [], [tradesQuery.data?.trades]);
    const trades = useMemo(() => rawTrades.filter(isEvaluatedBacktestTrade), [rawTrades]);
    const unevaluatedTradeCount = rawTrades.length - trades.length;

    const optionsToFetch = useMemo(() => getPortfolioBacktestOptionRequests(trades), [trades]);
    const optionsQuery = useAppQuery(
        {
            ...trpcPostUnbatched.entity.fetchOptions.queryOptions({ options: optionsToFetch }),
            enabled: optionsToFetch.length > 0
        },
        { fallbackErrorMessage: 'Failed to load option exposure details', setError: (msg) => setError(msg) }
    );
    const tradeGroups = useMemo(
        () => groupPortfolioBacktestTrades(trades, optionsQuery.data?.options ?? []),
        [trades, optionsQuery.data?.options]
    );

    const exposureGroupOptions = underlyingExposureGroups
        .filter((group) => group === 'bullish' || group === 'bearish' || group === entityGrouping || tradeGroups[group].length > 0)
        .map((group) => ({ value: group, label: formatUnderlyingBias(group) }));

    const entityStats = useMemo(() => {
        if (!backtest) return [];
        // Keep every entity visible, including those with no trades in the selected group.
        const entitiesMap = new Map<string, { entityId: number; entityType: EntityType }>();
        for (const trade of trades) {
            const transaction = trade.transactions[0];
            if (transaction) {
                entitiesMap.set(`${transaction.entityId}:${transaction.entityType}`, {
                    entityId: transaction.entityId,
                    entityType: transaction.entityType
                });
            }
        }
        return [...entitiesMap.values()].map((entity) => ({
            ...entity,
            stats: getPerformanceForTrades({
                trades: tradeGroups[entityGrouping],
                ...entity,
                startingBalance: backtest.startingBalance
            })
        }));
    }, [trades, tradeGroups, entityGrouping, backtest]);

    // Compute derived stats when trades change
    const computedStats = useMemo(() => {
        if (!trades.length || !backtest)
            return {
                directionStats: [] as DirectionStat[],
                exposureStats: [] as DirectionStat[],
                yearStats: [] as YearStat[],
                monthStats: [] as MonthStat[],
                quarterStats: [] as QuarterStat[],
                researchWindowStats: [] as ResearchWindowStat[]
            };

        // Month ranges
        const createMonthRangesUsingTimestamps = (timestamps: number[]) => {
            const months: { start: number; end: number }[] = [];

            for (const timestamp of timestamps) {
                const startOfMonth = dayjs.unix(timestamp).startOf('month').unix();
                const findMonth = months.find((month) => month.start === startOfMonth);
                if (findMonth) continue;
                const endOfMonth = dayjs.unix(timestamp).endOf('month').unix();
                months.push({ start: startOfMonth, end: endOfMonth });
            }

            return months;
        };

        // Quarter ranges
        const getQuarterRangesUsingTimestamps = (timestamps: number[]) => {
            if (timestamps.length === 0) return [] as { start: number; end: number }[];
            const minTimestamp = Math.min(...timestamps);
            const maxTimestamp = Math.max(...timestamps);
            const minYear = dayjs.unix(minTimestamp).year();
            const maxYear = dayjs.unix(maxTimestamp).year();

            const quarters = [
                { startMonth: 1, endMonth: 3 },
                { startMonth: 4, endMonth: 6 },
                { startMonth: 7, endMonth: 9 },
                { startMonth: 10, endMonth: 12 }
            ];

            const returnQuarters: { start: number; end: number }[] = [];
            let didReachEndQuarter = false;

            for (let year = minYear; year <= maxYear; year++) {
                for (const quarter of quarters) {
                    const start = dayjs(`${year}-${quarter.startMonth}-01`).startOf('day').unix();

                    const end = dayjs(`${year}-${quarter.endMonth}-${quarter.endMonth === 2 ? 28 : 30}`)
                        .endOf('day')
                        .unix();

                    if (end > maxTimestamp) {
                        didReachEndQuarter = true;
                        returnQuarters.push({ start, end: maxTimestamp });
                        break;
                    }

                    returnQuarters.push({ start, end });
                }

                if (didReachEndQuarter) break;
            }

            return returnQuarters;
        };

        // Position-side stats
        const directionStats: DirectionStat[] = [];

        for (const direction of ['long', 'short'] as const) {
            const stats = getPerformanceForTrades({ trades, direction, startingBalance: backtest.startingBalance });
            directionStats.push({ direction: direction.charAt(0).toUpperCase() + direction.slice(1), stats });
        }

        const exposureStats = underlyingExposureGroups
            .filter((group) => group === 'bullish' || group === 'bearish' || tradeGroups[group].length > 0)
            .map((group) => ({
                direction: formatUnderlyingBias(group),
                stats: getPerformanceForTrades({ trades: tradeGroups[group], startingBalance: backtest.startingBalance })
            }));

        const timestamps = trades.flatMap((trade) => trade.transactions.map((txn) => txn.timestamp)).filter(Boolean);
        const yearStats: YearStat[] = [];
        const monthStats: MonthStat[] = [];
        const quarterStats: QuarterStat[] = [];

        if (timestamps.length) {
            // Years
            const distinctYears = new Set<number>();

            for (const ts of timestamps) {
                const { year } = getDatePieces(ts);
                distinctYears.add(year);
            }

            for (const year of distinctYears) {
                const tradesForYear = trades.filter((trade) => {
                    const firstTxn = trade.transactions[0];
                    if (!firstTxn?.timestamp) return false;
                    const { year: tradeYear } = getDatePieces(firstTxn.timestamp);
                    return tradeYear === year;
                });

                yearStats.push({
                    year: year.toString(),
                    stats: getPerformanceForTrades({ trades: tradesForYear, startingBalance: backtest.startingBalance })
                });
            }

            // Months
            const monthRanges = createMonthRangesUsingTimestamps(timestamps);

            for (const monthRange of monthRanges) {
                const tradesForMonth = trades.filter((trade) => {
                    const firstTxn = trade.transactions[0];
                    if (!firstTxn?.timestamp) return false;
                    return firstTxn.timestamp >= monthRange.start && firstTxn.timestamp <= monthRange.end;
                });

                const monthName = dayjs.unix(monthRange.start).format('MMM YYYY');
                monthStats.push({
                    month: monthName,
                    stats: getPerformanceForTrades({ trades: tradesForMonth, startingBalance: backtest.startingBalance })
                });
            }

            // Quarters
            const quarterRanges = getQuarterRangesUsingTimestamps(timestamps);

            for (const quarterRange of quarterRanges) {
                const tradesForQuarter = trades.filter((trade) => {
                    const firstTxn = trade.transactions[0];
                    if (!firstTxn?.timestamp) return false;
                    return firstTxn.timestamp >= quarterRange.start && firstTxn.timestamp <= quarterRange.end;
                });

                const { month, year } = getDatePieces(quarterRange.start);
                const quarter = Math.ceil(month / 3);
                quarterStats.push({
                    quarter: `${year} Q${quarter}`,
                    stats: getPerformanceForTrades({ trades: tradesForQuarter, startingBalance: backtest.startingBalance })
                });
            }
        }

        return {
            directionStats,
            exposureStats,
            yearStats,
            monthStats,
            quarterStats,
            researchWindowStats: computeResearchWindowStats(trades, backtest.trainingEndDateTime ?? null, backtest.startingBalance)
        };
    }, [trades, backtest, tradeGroups]);

    // Extract entities to fetch
    const entitiesToFetch = useMemo(() => {
        if (!trades.length) return [];

        // Use a Map to ensure uniqueness based on entity ID and type
        const entitiesMap = new Map<string, EntityObjectCompressed>();

        for (const trade of trades) {
            // Create a unique key for each entity
            const entityKey = `${trade.entityId}_${trade.entityType}`;

            // Only add if not already in the map
            if (!entitiesMap.has(entityKey)) {
                entitiesMap.set(entityKey, {
                    i: trade.entityId,
                    t: trade.entityType === 'Asset' ? 'a' : trade.entityType === 'Cryptocurrency' ? 'c' : 'i'
                });
            }
        }

        // Convert Map values to an array for the API call
        return Array.from(entitiesMap.values());
    }, [trades]);

    // Fetch entities using the batched hook
    const { entities, isLoading: entitiesLoading } = useFetchEntities(entitiesToFetch);

    // Extract strategy IDs to fetch
    const strategyIds = useMemo(() => {
        if (!backtest?.strategiesSubscribed?.length) return [];
        return backtest.strategiesSubscribed.map((s) => s.id);
    }, [backtest]);

    // Fetch strategies
    const strategiesQuery = useAppQuery({
        ...trpc.strategy.fetchStrategies.queryOptions({ ids: strategyIds }),
        enabled: strategyIds.length > 0
    });

    // Extract strategies data
    const strategies = strategiesQuery.data?.strategies || [];

    // Calculate loading state
    const loading = useMemo(() => {
        return (
            backtestQuery.isLoading ||
            (backtest?.startDateTime && backtest?.endDateTime && riskFreeRateQuery.isLoading) ||
            tradesQuery.isLoading ||
            (optionsToFetch.length > 0 && optionsQuery.isLoading) ||
            (entitiesToFetch.length > 0 && entitiesLoading) ||
            (strategyIds.length > 0 && strategiesQuery.isLoading)
        );
    }, [
        backtestQuery.isLoading,
        backtest?.startDateTime,
        backtest?.endDateTime,
        riskFreeRateQuery.isLoading,
        tradesQuery.isLoading,
        optionsToFetch.length,
        optionsQuery.isLoading,
        entitiesToFetch.length,
        entitiesLoading,
        strategyIds.length,
        strategiesQuery.isLoading
    ]);

    // Handle query errors
    // Removed setError in effect for missing ID to avoid cascading renders

    useEffect(() => {
        if (riskFreeRateQuery.error && backtest?.startDateTime && backtest?.endDateTime) {
            handleTrpcQuery(riskFreeRateQuery, {
                fallbackErrorMessage: 'Failed to fetch risk-free rate',
                setError: (msg) => setError(msg)
            });
        }
    }, [riskFreeRateQuery.error, backtest?.startDateTime, backtest?.endDateTime]);

    useEffect(() => {
        if (strategiesQuery.error && strategyIds.length > 0) {
            handleTrpcQuery(strategiesQuery, {
                fallbackErrorMessage: 'Failed to fetch strategies',
                setError: (msg) => setError(msg)
            });
        }
    }, [strategiesQuery.error, strategyIds.length]);

    const getPortfolioName = () => {
        if (!backtest) return 'Portfolio Backtest Stats';

        let strategyNames = '';

        if (strategies.length > 0) {
            strategyNames = strategies.map((s) => s.name).join(', ');
        }

        return `${strategyNames} - ${backtest.name} ${backtest.startDateTime} - ${backtest.endDateTime}`;
    };

    // Column definitions for the main stats table
    const statsColumns = [
        {
            header: 'Avg Account Return / Trade',
            accessorKey: 'avgProfitPercentage',
            minSize: 100,
            size: 150,
            maxSize: 200
        },
        {
            header: backtest?.riskStatistics?.length ? 'Max Drawdown (1m MTM)' : 'Max Drawdown (legacy)',
            accessorKey: 'maxDrawdown',
            minSize: 80,
            size: 120,
            maxSize: 180
        },
        {
            header: backtest?.riskStatistics?.length ? 'Sharpe (daily)' : 'Sharpe Ratio (legacy)',
            accessorKey: 'sharpeRatio',
            minSize: 80,
            size: 120,
            maxSize: 180
        },
        {
            header: 'Pain Index',
            accessorKey: 'painIndex',
            minSize: 80,
            size: 120,
            maxSize: 180
        },
        {
            header: 'CAGR',
            accessorKey: 'cagr',
            minSize: 100,
            size: 150,
            maxSize: 200
        },
        {
            header: 'SPY %',
            accessorKey: 'spyReturnPercent',
            minSize: 80,
            size: 120,
            maxSize: 180
        },
        {
            header: 'Universe B&H %',
            accessorKey: 'universeBuyHoldReturnPercent',
            minSize: 100,
            size: 140,
            maxSize: 200
        },
        {
            // Random-entry CONTROL; supported-structure + gross-vs-net caveats in the shared disclaimer tooltip.
            header: () => <span title={backtest ? getTimingEdgeTooltip(backtest) : TIMING_EDGE_DISCLAIMER}>Timing Edge %</span>,
            accessorKey: 'timingEdgePercent',
            minSize: 100,
            size: 140,
            maxSize: 200
        },
        {
            header: 'Volatility',
            accessorKey: 'volatility',
            minSize: 80,
            size: 120,
            maxSize: 180
        },
        {
            header: 'Sortino Ratio',
            accessorKey: 'sortinoRatio',
            minSize: 80,
            size: 120,
            maxSize: 180
        },
        {
            header: 'Max Concurrent Positions',
            accessorKey: 'maxConcurrentPositions',
            minSize: 120,
            size: 180,
            maxSize: 250
        },
        {
            header: 'Average Daily Capital',
            accessorKey: 'averageRiskCapital',
            minSize: 120,
            size: 180,
            maxSize: 250
        },
        {
            header: 'Arorc',
            accessorKey: 'arorc',
            minSize: 60,
            size: 100,
            maxSize: 150
        },
        {
            header: 'Avg. Positions',
            accessorKey: 'avgPositions',
            minSize: 80,
            size: 120,
            maxSize: 180
        }
    ];

    const strategyStatsColumns: ColumnDef<StrategyAttributionRow>[] = [
        { header: 'Strategy', accessorKey: 'strategy', minSize: 160, size: 240, maxSize: 320 },
        { header: 'Window', accessorKey: 'window', minSize: 160, size: 220, maxSize: 280 },
        { header: 'Candidates', accessorKey: 'candidateTrades', minSize: 80, size: 110, maxSize: 140 },
        { header: 'Accepted', accessorKey: 'acceptedTrades', minSize: 80, size: 100, maxSize: 130 },
        { header: 'Skipped', accessorKey: 'skippedTrades', minSize: 80, size: 100, maxSize: 130 },
        {
            header: 'Accepted P&L',
            accessorKey: 'acceptedProfit',
            minSize: 100,
            size: 140,
            maxSize: 180,
            cell: ({ row }: CellContext<StrategyAttributionRow>) => (
                <StrategyPerformanceValue value={row.original.acceptedProfit} format="currency" />
            )
        },
        {
            header: 'P&L Contribution',
            accessorKey: 'profitContribution',
            minSize: 110,
            size: 150,
            maxSize: 190,
            cell: ({ row }: CellContext<StrategyAttributionRow>) => (
                <StrategyPerformanceValue value={row.original.profitContribution} format="percent" />
            )
        },
        {
            header: 'Avg. Trade %',
            accessorKey: 'averageTradeProfit',
            minSize: 90,
            size: 120,
            maxSize: 150,
            cell: ({ row }: CellContext<StrategyAttributionRow>) => (
                <StrategyPerformanceValue value={row.original.averageTradeProfit} format="percent" />
            )
        },
        {
            header: 'Median Trade %',
            accessorKey: 'medianTradeProfit',
            minSize: 100,
            size: 130,
            maxSize: 160,
            cell: ({ row }: CellContext<StrategyAttributionRow>) => (
                <StrategyPerformanceValue value={row.original.medianTradeProfit} format="percent" />
            )
        },
        {
            header: 'Win Rate',
            accessorKey: 'winRate',
            minSize: 80,
            size: 110,
            maxSize: 140,
            cell: ({ row }: CellContext<StrategyAttributionRow>) => (
                <StrategyPerformanceValue value={row.original.winRate} format="percent" positiveThreshold={50} />
            )
        },
        {
            header: 'Profit Factor',
            accessorKey: 'profitFactor',
            minSize: 90,
            size: 120,
            maxSize: 150,
            cell: ({ row }: CellContext<StrategyAttributionRow>) => (
                <StrategyPerformanceValue value={row.original.profitFactor} format="number" positiveThreshold={1} />
            )
        },
        {
            header: 'Arorc',
            accessorKey: 'returnOnRiskCapital',
            minSize: 80,
            size: 110,
            maxSize: 150,
            cell: ({ row }: CellContext<StrategyAttributionRow>) => (
                <StrategyPerformanceValue value={row.original.returnOnRiskCapital} format="percent" />
            )
        },
        {
            header: 'Avg. Risk Capital',
            accessorKey: 'averageRiskCapital',
            minSize: 120,
            size: 160,
            maxSize: 200,
            cell: ({ row }: CellContext<StrategyAttributionRow>) =>
                row.original.averageRiskCapital === null ? '-' : formatCurrency(row.original.averageRiskCapital)
        }
    ];

    // Column definitions for the direction/entity/time tables
    const commonStatsColumns: ColumnDef<PerformanceStatsRow>[] = [
        {
            header: 'Starting Balance',
            accessorKey: 'startingBalance',
            minSize: 100,
            size: 150,
            maxSize: 200
        },
        {
            header: 'Ending Balance',
            accessorKey: 'endingBalance',
            minSize: 100,
            size: 150,
            maxSize: 200
        },
        {
            header: 'Number of Trades',
            accessorKey: 'numberOfTrades',
            minSize: 80,
            size: 120,
            maxSize: 180
        },
        {
            header: 'Avg. Profit %',
            accessorKey: 'avgProfitPercentage',
            minSize: 80,
            size: 120,
            maxSize: 180,
            cell: ({ row }) => <StrategyPerformanceValue value={row.original.avgProfitPercentage} format="percent" />
        },
        {
            header: 'Profit',
            accessorKey: 'profit',
            minSize: 80,
            size: 125,
            maxSize: 180,
            cell: ({ row }) => <StrategyPerformanceValue value={row.original.profit} format="currency" />
        },
        {
            header: 'Profit %',
            accessorKey: 'profitPercentage',
            minSize: 80,
            size: 120,
            maxSize: 180,
            cell: ({ row }) => <StrategyPerformanceValue value={row.original.profitPercentage} format="percent" />
        },
        {
            header: 'Arorc',
            accessorKey: 'arorc',
            minSize: 60,
            size: 100,
            maxSize: 150,
            cell: ({ row }) => <StrategyPerformanceValue value={row.original.arorc} format="percent" />
        },
        {
            header: 'Average Risk Capital',
            accessorKey: 'averageRiskCapital',
            minSize: 120,
            size: 180,
            maxSize: 250,
            cell: ({ row }) => row.original.averageRiskCapital ?? '—'
        },
        {
            header: 'Average Positions',
            accessorKey: 'averagePositions',
            minSize: 100,
            size: 150,
            maxSize: 200,
            cell: ({ row }) => row.original.averagePositions ?? '—'
        }
    ];

    let statsData: any[] = [];
    const diagnostics = backtest?.diagnostics ?? null;

    const strategyTableData: StrategyAttributionRow[] = strategyStats.flatMap((strategyStat) => {
        const strategyName = strategies.find((strategy) => strategy.id === strategyStat.strategyId)?.name;
        const strategyLabel = `${strategyName ?? 'Strategy'} (#${strategyStat.strategyId})`;
        const buildWindowRow = (window: string, windowStats: typeof strategyStat.training): StrategyAttributionRow => ({
            strategy: strategyLabel,
            window,
            candidateTrades: windowStats.candidateTradeCount,
            acceptedTrades: windowStats.acceptedTradeCount,
            skippedTrades: windowStats.skippedTradeCount,
            acceptedProfit: windowStats.acceptedProfit,
            profitContribution: windowStats.profitContributionPercent,
            averageTradeProfit: windowStats.averageTradeProfitPercent,
            medianTradeProfit: windowStats.medianTradeProfitPercent,
            winRate: windowStats.winRatePercent,
            profitFactor: windowStats.profitFactor,
            returnOnRiskCapital: windowStats.returnOnRiskCapital,
            averageRiskCapital: windowStats.averageRiskCapital
        });

        if (!backtest?.trainingEndDateTime) return [buildWindowRow('All entries — split unavailable', strategyStat)];
        return [
            buildWindowRow('Backtest (in-sample)', strategyStat.training),
            buildWindowRow('Walk-forward (out-of-sample)', strategyStat.walkForward)
        ].filter((row) => row.candidateTrades > 0 || row.acceptedTrades > 0);
    });

    if (backtest) {
        const stats = getPerformanceForTrades({ trades, startingBalance: backtest.startingBalance });
        const maxDrawdown = backtest.maxDrawdown ?? null;

        const sharpeRatio = backtest.sharpeRatio ?? null;

        const marketValued = !!backtest.riskStatistics?.length;
        const chart = backtest.postTradeBalances ?? [];
        const daily = marketValued ? dailyBalanceSnapshots(chart) : chart;
        const periodsPerYear = marketValued ? 365.25 : 252;
        const dailyBenchmark = marketValued ? Math.expm1((Math.log1p(riskFreeRate) * 252) / 365.25) : riskFreeRate;
        const volatility = calculateVolatilityFromPostTradeBalances(daily, periodsPerYear);
        const sortinoRatio = calculateSortinoRatioFromPostTradeBalances(daily, dailyBenchmark, periodsPerYear);
        const painIndex = calculatePainIndexFromPostTradeBalances(daily);
        const cagr = calculatePortfolioCagr(backtest);
        const maxConcurrentTrades = calculateMaxConcurrentTrades(trades);
        const timingEdgeCoverageLabel = getTimingEdgeCoverageLabel(backtest);

        statsData = [
            {
                avgProfitPercentage:
                    stats.numberOfTrades > 0 && Number.isFinite(stats.avgTradeProfitPercentage)
                        ? `${stats.avgTradeProfitPercentage}%`
                        : '—',
                maxDrawdown: maxDrawdown === null ? '-' : `${maxDrawdown}%`,
                sharpeRatio: sharpeRatio ?? '—',
                painIndex: backtest.replenishmentAmount ? '—' : `${painIndex}%`,
                cagr: cagr === null ? '—' : `${cagr.toFixed(2)}%`,
                spyReturnPercent: backtest.spyReturnPercent != null ? `${trimDecimals(backtest.spyReturnPercent, 2)}%` : '-',
                universeBuyHoldReturnPercent:
                    backtest.universeBuyHoldReturnPercent != null ? `${trimDecimals(backtest.universeBuyHoldReturnPercent, 2)}%` : '-',
                timingEdgePercent:
                    backtest.timingEdgePercent != null
                        ? `${trimDecimals(backtest.timingEdgePercent, 2)}%${timingEdgeCoverageLabel ? ` (${timingEdgeCoverageLabel})` : ''}`
                        : '-',
                volatility: backtest.replenishmentAmount ? '—' : `${volatility}%`,
                sortinoRatio: backtest.replenishmentAmount || !Number.isFinite(sortinoRatio) ? '—' : sortinoRatio,
                maxConcurrentPositions: maxConcurrentTrades,
                averageRiskCapital: stats.averageRiskCapitalPerDay,
                arorc: stats.returnOnRiskCapital,
                avgPositions: stats.averagePositions
            }
        ];
    }

    const exposureTableData = computedStats.exposureStats.map((item) => ({
        direction: item.direction,
        ...buildPerformanceStatsRow(item.stats)
    }));

    const directionTableData = computedStats.directionStats.map((item) => ({
        direction: item.direction,
        ...buildPerformanceStatsRow(item.stats)
    }));

    // Data for entity table
    const entityTableData = entityStats.map((item) => ({
        entity: entities.find((e) => e.id === item.entityId && e.entityType === item.entityType)?.symbol,
        ...buildPerformanceStatsRow(item.stats)
    }));

    // Data for research-window (training vs walk-forward) table
    const researchWindowTableData = computedStats.researchWindowStats.map((item) => ({
        window: item.window,
        ...buildPerformanceStatsRow(item.stats)
    }));

    // Data for year table
    const yearTableData = computedStats.yearStats.map((item) => ({
        year: item.year,
        ...buildPerformanceStatsRow(item.stats)
    }));

    // Data for month table
    const monthTableData = computedStats.monthStats.map((item) => ({
        month: item.month,
        ...buildPerformanceStatsRow(item.stats)
    }));

    // Data for quarter table
    const quarterTableData = computedStats.quarterStats.map((item) => ({
        quarter: item.quarter,
        ...buildPerformanceStatsRow(item.stats)
    }));

    return (
        <UserLayout title="Portfolio Backtest Stats">
            <div className="w-full mt-3 px-4">
                <div className="flex justify-between">
                    <div>
                        <h3 className="text-white mb-3">{getPortfolioName()}</h3>
                    </div>
                </div>

                {(!portfolioBacktestId || error) && <Alert variant="danger">{error || 'No portfolio backtest ID provided'}</Alert>}

                {loading ? (
                    <div className="flex justify-center items-center min-h-[60vh]">
                        <DataLoadingSpinner text="Loading backtest data..." />
                    </div>
                ) : (
                    <>
                        {backtest && (
                            <div className="mb-4 flex items-start gap-4 rounded border border-zinc-700 p-3">
                                <div className="flex-1">
                                    <h4 className="mb-2">Automatic refresh</h4>
                                    {refreshQuery.data ? (
                                        <PortfolioBacktestRefreshSummary backtestId={backtest.id} status={refreshStatus} />
                                    ) : (
                                        <p>{refreshQuery.isLoading ? 'Loading refresh settings…' : 'Refresh settings unavailable'}</p>
                                    )}
                                </div>
                                {refreshQuery.data && (
                                    <PortfolioBacktestRefreshControl
                                        backtest={backtest}
                                        status={refreshStatus}
                                        onChanged={() => {
                                            void refreshQuery.refetch();
                                        }}
                                    />
                                )}
                            </div>
                        )}
                        <PortfolioInputCoverageSummary diagnostics={diagnostics} />
                        {diagnostics?.capitalStarvation.isDetected && (
                            <Alert variant="warning">
                                Capital starvation detected. The portfolio stopped funding trades well before the end of the backtest
                                window.
                            </Alert>
                        )}

                        {unevaluatedTradeCount > 0 && (
                            <Alert variant="warning">
                                {unevaluatedTradeCount} accepted {unevaluatedTradeCount === 1 ? 'trade was' : 'trades were'} excluded from
                                derived statistics because canonical profit economics were unavailable.
                            </Alert>
                        )}

                        {frozenExitCandidateSelections.length > 0 && (
                            <div className="mt-3 rounded border border-zinc-700 bg-zinc-900/60 p-4">
                                <h4 className="mb-1">Frozen Exit Candidate Selections</h4>
                                <p className="mb-3 text-xs text-zinc-400">
                                    These are the exact global candidates captured when this backtest was launched.
                                </p>
                                <div className="grid gap-3 md:grid-cols-2">
                                    {frozenExitCandidateSelections.map((selection) => {
                                        const strategyName =
                                            strategies.find((strategy) => strategy.id === selection.strategyId)?.name ??
                                            `Strategy ${selection.strategyId}`;

                                        return (
                                            <div className="rounded border border-zinc-700 bg-zinc-950/60 p-3" key={selection.strategyId}>
                                                <div className="mb-2 text-sm font-medium text-zinc-100">
                                                    {strategyName}
                                                    <span className="ml-2 font-mono text-xs text-zinc-500">#{selection.strategyId}</span>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {selection.candidates.map((candidate) => (
                                                        <Link
                                                            className="rounded bg-zinc-800 px-2 py-1 text-xs text-blue-200 hover:bg-zinc-700"
                                                            key={candidate.id}
                                                            to={`/user/exitCandidate/${candidate.id}`}
                                                        >
                                                            {candidate.label} · #{candidate.id}
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <PriceEntrySetupSummary summary={priceEntrySetups} />

                        {backtest?.postTradeBalances && (
                            <PerformanceChart balances={backtest.postTradeBalances} height={300} lineColor="#2962FF" />
                        )}

                        {diagnostics && (
                            <div className="p-4 mt-3">
                                <h4 className="mb-3">Capital Diagnostics</h4>
                                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                    <div className="rounded border border-zinc-700 bg-zinc-900/60 p-3">
                                        <div className="text-xs uppercase tracking-wide text-zinc-400">Candidate / Accepted / Skipped</div>
                                        <div className="mt-2 text-lg text-white">
                                            {diagnostics.candidateTradeCount} / {diagnostics.acceptedTradeCount} /{' '}
                                            {diagnostics.skippedTradeCount}
                                        </div>
                                    </div>
                                    <div className="rounded border border-zinc-700 bg-zinc-900/60 p-3">
                                        <div className="text-xs uppercase tracking-wide text-zinc-400">Skipped For Insufficient Funds</div>
                                        <div className="mt-2 text-lg text-white">{diagnostics.skippedInsufficientFundsCount}</div>
                                    </div>
                                    <div className="rounded border border-zinc-700 bg-zinc-900/60 p-3">
                                        <div className="text-xs uppercase tracking-wide text-zinc-400">Accepted / Skipped P&amp;L</div>
                                        <div className="mt-2 text-lg text-white">
                                            ${trimDecimals(diagnostics.acceptedTradesProfit)} / $
                                            {trimDecimals(diagnostics.skippedTradesProfit)}
                                        </div>
                                    </div>
                                    <div className="rounded border border-zinc-700 bg-zinc-900/60 p-3">
                                        <div className="text-xs uppercase tracking-wide text-zinc-400">Last Funded Trade</div>
                                        <div className="mt-2 text-lg text-white">
                                            {diagnostics.lastAcceptedTradeTimestamp
                                                ? dayjs.unix(diagnostics.lastAcceptedTradeTimestamp).format('MMM D, YYYY')
                                                : 'None'}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 text-sm text-zinc-300">
                                    Days without a funded trade before end: {diagnostics.capitalStarvation.daysWithoutFundedTradesBeforeEnd}
                                </div>
                            </div>
                        )}

                        {backtest?.taxConfiguration && (
                            <PortfolioBacktestTaxComparison
                                configuration={backtest.taxConfiguration}
                                comparison={backtest.taxComparison}
                                baselineBalances={backtest.postTradeBalances ?? []}
                            />
                        )}
                        <div className="p-4 mt-3">
                            <h4 className="mb-1">Overall Statistics</h4>
                            <p className="mb-3 text-xs text-zinc-400">
                                {backtest?.riskStatistics?.length
                                    ? 'Drawdown includes open-position gains and losses at minute observations. Sharpe uses daily excess portfolio returns, including idle days. '
                                    : 'Legacy risk statistics: open positions were carried at cost. Recalculation is required for market-valued drawdown and daily Sharpe. '}
                                CAGR is annualized portfolio growth over the saved valuation period, excluding external replenishments,
                                using a 365.25-day year. It is unavailable without saved market-valued returns. Avg Account Return / Trade
                                uses this run's starting balance and divides account return by trade count, not by position premium. Average
                                Daily Capital is aggregate daily exposure, not the per-trade budget. SPY % and Universe B&amp;H % are
                                context benchmarks (ticker quality / market), not random-entry controls — they ignore trade frequency,
                                sizing, side, options, and timing. Timing Edge % is the random-entry control: the average of each
                                trade&apos;s return minus the random-entry baseline for the same name, month, and horizon (&apos;-&apos;
                                while baselines are pending).
                            </p>
                            <DataTable striped columns={statsColumns} data={statsData} search={false} />
                        </div>

                        {!!backtest?.riskStatistics?.length && (
                            <div className="p-4 mt-3">
                                <h4 className="mb-1">Market-valued Portfolio Windows</h4>
                                <p className="mb-3 text-xs text-zinc-400">
                                    Each window includes the change in all positions held during that period, including trades opened
                                    earlier. These calendar-period returns differ from the entry-cohort trade statistics below. Drawdown is
                                    measured within each window. Stock prices carry the last observed print between updates; missing
                                    sessions fail valuation.
                                </p>
                                <DataTable
                                    striped
                                    search={false}
                                    columns={[
                                        { header: 'Window', accessorKey: 'window' },
                                        { header: 'Starting Equity', accessorKey: 'startingEquity' },
                                        { header: 'Ending Equity', accessorKey: 'endingEquity' },
                                        { header: 'Return', accessorKey: 'return' },
                                        { header: 'Drawdown (1m)', accessorKey: 'drawdown' },
                                        { header: 'Drawdown (daily)', accessorKey: 'dailyDrawdown' },
                                        { header: 'Sharpe (daily)', accessorKey: 'sharpe' }
                                    ]}
                                    data={backtest.riskStatistics.map((row) => ({
                                        window: { overall: 'Full range', training: 'Training', walkForward: 'Walk-forward' }[row.period],
                                        startingEquity: currencyFormatter.format(row.startingEquity),
                                        endingEquity: currencyFormatter.format(row.endingEquity),
                                        return: `${trimDecimals(row.returnPercent, 2)}%`,
                                        drawdown: `${trimDecimals(row.maxDrawdown, 2)}%`,
                                        dailyDrawdown: `${trimDecimals(row.dailyMaxDrawdown, 2)}%`,
                                        sharpe: row.sharpeRatio ?? '—'
                                    }))}
                                />
                            </div>
                        )}

                        {entryTimingProfiles.length > 0 && <PortfolioBacktestEntryTimingProfiles profiles={entryTimingProfiles} />}

                        {strategyTableData.length > 0 && (
                            <div className="p-4 mt-3">
                                <h4 className="mb-1">Strategy Attribution</h4>
                                <p className="mb-3 text-xs text-zinc-400">
                                    Saved walk-forward start: {backtest?.trainingEndDateTime ?? 'Unavailable for this run'}. Performance
                                    uses the resized trades actually accepted by this portfolio; P&amp;L contribution is calculated within
                                    each window and may be negative.
                                </p>
                                <DataTable striped columns={strategyStatsColumns} data={strategyTableData} search={false} />
                            </div>
                        )}

                        <div className="p-4 mt-3">
                            <h4 className="mb-1">Research Window Statistics</h4>
                            <p className="mb-3 text-xs text-zinc-400">
                                Training = entries before the saved cutoff ({backtest?.trainingEndDateTime ?? 'unavailable'}); walk-forward
                                = entries at or after it. End Date is an entry cutoff; accepted trades may close later. Reused walk-forward
                                data is research evidence, not a fresh holdout.
                            </p>
                            {!backtest?.trainingEndDateTime && (
                                <Alert variant="warning">
                                    This run has no saved research cutoff. No training/walk-forward split is shown.
                                </Alert>
                            )}
                            {backtest?.trainingEndDateTime && backtest.startDateTime >= backtest.trainingEndDateTime && (
                                <Alert variant="info">Walk-forward-only run: the requested entry window contains no training period.</Alert>
                            )}
                            {researchWindowTableData.length > 0 &&
                                !researchWindowTableData.some((row) => row.window.startsWith('Walk-forward')) && (
                                    <Alert variant="warning">
                                        No walk-forward trades: this backtest has no entries after the development cutoff, so it provides no
                                        out-of-sample evidence.
                                    </Alert>
                                )}
                            <DataTable
                                striped
                                columns={[
                                    { header: 'Window', accessorKey: 'window', minSize: 140, size: 220, maxSize: 280 },
                                    ...commonStatsColumns
                                ]}
                                data={researchWindowTableData}
                                search={false}
                            />
                        </div>

                        <div className="p-4 mt-3">
                            <h4 className="mb-1">Underlying exposure statistics</h4>
                            <p className="mb-3 text-xs text-zinc-400">
                                Bullish and bearish describe exposure to the underlying price. A bought call is long and bullish; a bought
                                put is long and bearish. Mixed combines opposing leg exposures; Unknown means exposure could not be
                                determined. Each trade is counted once in this table.
                            </p>
                            <DataTable
                                striped
                                columns={[
                                    { header: 'Underlying exposure', accessorKey: 'direction', minSize: 130, size: 160, maxSize: 220 },
                                    ...commonStatsColumns
                                ]}
                                data={exposureTableData}
                                search={false}
                            />
                        </div>

                        <div className="p-4 mt-3">
                            <h4 className="mb-1">Position-side statistics</h4>
                            <p className="mb-3 text-xs text-zinc-400">
                                Long and short describe ownership of the instrument. Both bought calls and bought puts are long positions.
                            </p>
                            <DataTable
                                striped
                                columns={[
                                    { header: 'Position side', accessorKey: 'direction', minSize: 90, size: 130, maxSize: 180 },
                                    ...commonStatsColumns
                                ]}
                                data={directionTableData}
                                search={false}
                            />
                        </div>

                        <div className="p-4 mt-3">
                            <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                                <div>
                                    <h4 className="mb-1">Entity Statistics</h4>
                                    <p className="text-xs text-zinc-400">
                                        Compare all trades, underlying exposure, or position side for each entity.
                                    </p>
                                </div>
                                <FormSelect
                                    label="Trade group"
                                    aria-label="Entity statistics trade group"
                                    className="min-w-48"
                                    fullWidth={false}
                                    noMargin
                                    onChange={(event) => setEntityGrouping(event.target.value as EntityStatsGrouping)}
                                    options={[
                                        { value: 'all', label: 'All' },
                                        ...exposureGroupOptions,
                                        { value: 'long', label: 'Long' },
                                        { value: 'short', label: 'Short' }
                                    ]}
                                    size="sm"
                                    value={entityGrouping}
                                />
                            </div>
                            <DataTable
                                striped
                                columns={[
                                    { header: 'Entity', accessorKey: 'entity', minSize: 100, size: 150, maxSize: 250 },
                                    ...commonStatsColumns
                                ]}
                                data={entityTableData}
                                search={false}
                            />
                        </div>

                        <div className="p-4 mt-3">
                            <h4 className="mb-3">Year Statistics</h4>
                            <DataTable
                                striped
                                columns={[
                                    { header: 'Year', accessorKey: 'year', minSize: 60, size: 80, maxSize: 120 },
                                    ...commonStatsColumns
                                ]}
                                data={yearTableData}
                                search={false}
                            />
                        </div>

                        <div className="p-4 mt-3">
                            <h4 className="mb-3">Month Statistics</h4>
                            <DataTable
                                striped
                                columns={[
                                    { header: 'Month', accessorKey: 'month', minSize: 80, size: 120, maxSize: 180 },
                                    ...commonStatsColumns
                                ]}
                                data={monthTableData}
                                search={false}
                            />
                        </div>

                        <div className="p-4 mt-3">
                            <h4 className="mb-3">Quarter Statistics</h4>
                            <DataTable
                                striped
                                columns={[
                                    { header: 'Quarter', accessorKey: 'quarter', minSize: 80, size: 120, maxSize: 180 },
                                    ...commonStatsColumns
                                ]}
                                data={quarterTableData}
                                search={false}
                            />
                        </div>
                    </>
                )}
            </div>
        </UserLayout>
    );
};

export default PortfolioBacktestStats;
